import { trackEvent } from '@zep/logger/tracking';

import { type AiBuilderUsageInfo } from '../stores/types';

import {
  type AiBuilderAuthExpiredPlace,
  type AiBuilderChatRestoreResult,
  type AiBuilderChatSnapshotSkipReason,
  type AiBuilderTrackedTurnIntent,
  type AiBuilderUsageBlockedPlace,
} from './events.quiz-ai-builder';
import {
  type AiBuilderChatOrchestrationEvent,
  type AiBuilderChatQuestionSnapshotReplaceEvent,
  type AiBuilderTurnRequest,
} from './types';

type AiBuilderTurnEventStream =
  | Iterable<AiBuilderChatOrchestrationEvent>
  | AsyncIterable<AiBuilderChatOrchestrationEvent>;

const GENERATION_CHANGE_TYPES: ReadonlySet<
  AiBuilderChatQuestionSnapshotReplaceEvent['change_type']
> = new Set(['create', 'replace']);

const toTrackedTurnIntent = (
  request: AiBuilderTurnRequest,
): AiBuilderTrackedTurnIntent => request.analyticsIntent ?? request.intent.type;

const hasSourceType = (request: AiBuilderTurnRequest, sourceType: string) =>
  (request.context.sourceTypes ?? []).includes(sourceType);

/**
 * 서버 턴 orchestration event stream 을 통과시키며 분석 이벤트를 발화하는 패스스루 래퍼.
 * 스트림 내용/순서는 변경하지 않는다 (behavior 무변경).
 *
 * - turn_start / turn_end / turn_error / turn_abort: Turn Success Rate
 * - question_change: Applied Operation / Undo Rate (change_type='undo')
 * - choice_view: Repeated Clarification Rate
 * - biz_ai_generation_completed: 구 모달 플로우와의 pre/post 브리지.
 *   스트림 중간이 아니라 턴 종료 시 generation_id 별 1회 발화한다 — 적용(apply)이
 *   이미 일어난 generation 은 턴이 중단/실패로 끝나도 완료로 집계한다.
 */
export async function* trackAiBuilderTurnAnalyticsEvents(
  events: AiBuilderTurnEventStream,
  request: AiBuilderTurnRequest,
): AsyncIterable<AiBuilderChatOrchestrationEvent> {
  const startedAt = Date.now();
  const intent = toTrackedTurnIntent(request);
  const clientRequestId = request.clientRequestId;
  const draftId = request.context.draftInstanceId;
  let sessionId = request.context.sessionId ?? '';
  let questionChangedCount = 0;
  let failureReason: string | null = null;
  let streamConsumed = false;
  /** 턴 중 draft 에 적용된 generation 별 문항 수 — 턴 종료 시 브리지 발화 재료. */
  const appliedGenerationQuizCounts = new Map<string, number>();

  trackEvent('quiz_ai_builder_turn_start', {
    intent,
    decision: request.intent.type === 'confirm' ? request.intent.decision : '',
    // sessionId 유무는 첫 턴 신호가 아니다 — 패널 오픈 시 세션이 선생성된다.
    // requestId 는 대화 시작(restart 포함)마다 1부터 증가하므로 1 이 곧 첫 턴.
    is_session_first: request.requestId === 1,
    session_id: sessionId,
    client_request_id: clientRequestId,
    draft_id: draftId,
    source_count: request.context.sourceRefs.length,
    has_file_source: hasSourceType(request, 'file'),
    has_youtube_source: hasSourceType(request, 'youtube'),
    has_website_source: hasSourceType(request, 'website'),
    has_selected_question: request.context.selectedQuestionContext !== null,
  });

  try {
    for await (const event of events) {
      if (event.type === 'setSessionId') {
        sessionId = event.sessionId;
      }

      // 소비자가 이벤트 적용을 끝낸 뒤에만 성공 지표를 기록한다. yield 중 소비자가
      // throw 하면 generator 는 return 되어 아래 성공 기록까지 진행하지 않는다.
      yield event;

      if (event.type === 'replaceQuestionSnapshots') {
        questionChangedCount += event.changed_question_ids.length;
        trackEvent('quiz_ai_builder_question_change', {
          change_type: event.change_type,
          changed_count: event.changed_question_ids.length,
          total: event.total,
          generation_id: event.generationId ?? '',
          session_id: sessionId,
          client_request_id: clientRequestId,
          draft_id: draftId,
        });

        if (
          event.generationId &&
          GENERATION_CHANGE_TYPES.has(event.change_type)
        ) {
          appliedGenerationQuizCounts.set(
            event.generationId,
            (appliedGenerationQuizCounts.get(event.generationId) ?? 0) +
              event.changed_question_ids.length,
          );
        }
      }

      if (event.type === 'applyGeneratedQuizzes') {
        questionChangedCount += event.quizzes.length;
        appliedGenerationQuizCounts.set(
          event.generationId,
          (appliedGenerationQuizCounts.get(event.generationId) ?? 0) +
            event.quizzes.length,
        );
      }

      if (event.type === 'append' || event.type === 'replace') {
        const message = event.message;

        if (message.kind === 'choice') {
          trackEvent('quiz_ai_builder_choice_view', {
            choice_kind: 'choice',
            option_count: message.options.length,
            session_id: sessionId,
            client_request_id: clientRequestId,
          });
        }

        if (message.kind === 'confirm') {
          trackEvent('quiz_ai_builder_choice_view', {
            choice_kind: 'confirm',
            option_count: 2,
            session_id: sessionId,
            client_request_id: clientRequestId,
          });
        }

        if (message.kind === 'system' && message.tone === 'error') {
          failureReason = message.errorCode ?? 'server_error';
        }
      }
    }
    streamConsumed = true;
  } catch (error) {
    failureReason = request.context.signal?.aborted
      ? 'aborted'
      : 'stream_error';
    throw error;
  } finally {
    if (!failureReason && request.context.signal?.aborted) {
      failureReason = 'aborted';
    } else if (!failureReason && !streamConsumed) {
      failureReason = 'consumer_error';
    }

    // draft 적용이 실제로 일어난 시점 기준의 "생성 완료" — 스트림이 끝난 뒤 1회.
    for (const [generationId, quizCount] of appliedGenerationQuizCounts) {
      trackEvent('biz_ai_generation_completed', {
        generation_id: generationId,
        prompt_type: request.context.sourceRefs.length > 0 ? 'source' : 'query',
        quizCount,
        ai_builder_session_id: sessionId,
        client_request_id: clientRequestId,
      });
    }

    const turnResultProps = {
      intent,
      session_id: sessionId,
      client_request_id: clientRequestId,
      latency_ms: Date.now() - startedAt,
    };

    if (failureReason === 'aborted') {
      // 유저 중단은 실패가 아니다 — 에러율 오염을 막기 위해 별도 이벤트로 분리.
      trackEvent('quiz_ai_builder_turn_abort', turnResultProps);
    } else if (failureReason) {
      trackEvent('quiz_ai_builder_turn_error', {
        ...turnResultProps,
        failure_reason: failureReason,
      });
    } else {
      trackEvent('quiz_ai_builder_turn_end', {
        ...turnResultProps,
        question_changed_count: questionChangedCount,
      });
    }
  }
}

/**
 * 로그인 만료 감지 1회당 1회. 발화 책임은 만료 단일 진입점
 * (`handleAiBuilderAuthExpired`)에 있다 — 중복 가드가 거기 있기 때문이다.
 */
export const trackAiBuilderAuthExpired = (params: {
  place: AiBuilderAuthExpiredPlace;
  sessionId: string;
  messageCount: number;
  hadActiveTurn: boolean;
  snapshotSkipReason: AiBuilderChatSnapshotSkipReason;
}) => {
  trackEvent('quiz_ai_builder_auth_expired_view', {
    place: params.place,
    session_id: params.sessionId,
    message_count: params.messageCount,
    had_active_turn: params.hadActiveTurn,
    snapshot_saved: params.snapshotSkipReason === '',
    snapshot_skip_reason: params.snapshotSkipReason,
  });
};

export const trackAiBuilderChatRestore = (params: {
  result: AiBuilderChatRestoreResult;
  sessionId: string;
  messageCount: number;
  sourceCount: number;
  ageMs: number;
}) => {
  trackEvent('quiz_ai_builder_chat_restore', {
    result: params.result,
    session_id: params.sessionId,
    message_count: params.messageCount,
    source_count: params.sourceCount,
    age_ms: params.ageMs,
  });
};

export const trackAiBuilderUsageBlocked = (
  usage: AiBuilderUsageInfo | null,
  place: AiBuilderUsageBlockedPlace,
) => {
  trackEvent('quiz_ai_builder_usage_limit_view', {
    place,
    remaining: usage?.remaining ?? 0,
    limit: usage?.limit ?? -1,
  });
};
